import React, { useEffect, useState } from "react";
import { Sparkles, ThumbsUp, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function HospitalAiSummary({ hospitalId }) {
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/ai/hospital/${hospitalId}/summary`);
        if (alive) { setSummary(data.summary); setState("ready"); }
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => { alive = false; };
  }, [hospitalId]);

  if (state === "error") return null;

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-5" data-testid="hospital-ai-summary">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-primary" />
        <div className="text-[11px] uppercase tracking-widest text-primary">AI quality summary</div>
      </div>
      {state === "loading" || !summary ? (
        <div className="mt-3 h-16 bg-white/60 rounded animate-pulse" />
      ) : (
        <>
          <p className="mt-2 text-sm text-primary leading-relaxed">{summary.one_liner}</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {summary.strengths?.length ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                  <ThumbsUp size={11} /> Strengths
                </div>
                <ul className="mt-1 space-y-1">
                  {summary.strengths.map((s, i) => (
                    <li key={i} className="text-xs text-primary flex items-start gap-1.5" data-testid={`hospital-ai-strength-${i}`}>
                      <span className="text-emerald-700">•</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {summary.concerns?.length ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-amber-800 flex items-center gap-1">
                  <AlertTriangle size={11} /> Watch
                </div>
                <ul className="mt-1 space-y-1">
                  {summary.concerns.map((c, i) => (
                    <li key={i} className="text-xs text-primary flex items-start gap-1.5" data-testid={`hospital-ai-concern-${i}`}>
                      <span className="text-amber-700">•</span>{c}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="mt-3 text-[10px] text-muted-foreground italic">
            Generated by AI from HCAHPS scores and public metadata. Refreshed daily.
          </div>
        </>
      )}
    </div>
  );
}
